import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ToolShell, OptionRow, ToolNav } from "@/pages/tools/_shared";

const PROJECT_TYPES = [
  { key: "custom-web", label: "Custom web platform", base: 12000 },
  { key: "mobile-app", label: "Mobile app (iOS + Android)", base: 15000 },
  { key: "saas", label: "SaaS product (multi-tenant)", base: 25000 },
  { key: "erp", label: "ERP / core operations", base: 30000 },
  { key: "grc", label: "GRC / compliance system", base: 20000 },
];
const TIMELINE_OPTS = ["Urgent (under 2 months)", "1–3 months", "3–6 months", "Just exploring"];

function band(cost) {
  if (cost < 8000) return "Under OMR 8,000";
  if (cost < 20000) return `OMR ${Math.round(cost - 3000).toLocaleString()} – ${Math.round(cost + 4000).toLocaleString()}`;
  if (cost < 50000) return `OMR ${Math.round(cost - 6000).toLocaleString()} – ${Math.round(cost + 10000).toLocaleString()}`;
  return `OMR ${Math.round(cost - 10000).toLocaleString()} – ${Math.round(cost + 20000).toLocaleString()}`;
}

function timelineRange(months) {
  const min = Math.max(1, months - 1);
  const max = months + 2;
  return `${min}–${max} months`;
}

export default function SoftwareCost() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ project_type: "", roles: 3, mobile: null, integrations: 2, timeline: "" });
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });

  const total = 6;

  const calc = () => {
    const pt = PROJECT_TYPES.find((p) => p.key === form.project_type) || PROJECT_TYPES[0];
    let cost = pt.base;
    cost += form.roles * 800;
    if (form.mobile) cost += 6000;
    cost += form.integrations * 1500;
    if (form.timeline === "Urgent (under 2 months)") cost *= 1.2;
    const months = form.timeline.includes("Urgent") ? 2 : form.timeline.includes("1–3") ? 3 : form.timeline.includes("3–6") ? 5 : 4;
    return { cost_band_omr: band(cost), timeline_range: timelineRange(months) };
  };

  if (state.stage === "result") {
    const { cost_band_omr, timeline_range } = state.result;
    return (
      <ToolShell eyebrow="Software Cost Estimator" title="Your indicative estimate">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6">
            <div className="text-xs uppercase tracking-wider text-wafa-ink/60">Indicative cost band</div>
            <div className="mt-3 font-heading text-3xl font-semibold text-wafa-deep">{cost_band_omr}</div>
          </div>
          <div className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-6">
            <div className="text-xs uppercase tracking-wider text-wafa-ink/60">Timeline range</div>
            <div className="mt-3 font-heading text-3xl font-semibold text-wafa-deep">{timeline_range}</div>
          </div>
        </div>
        <p className="mt-6 text-xs text-wafa-ink/60">
          <span className="font-semibold text-wafa-deep">Clearly indicative.</span> Real numbers come after we understand the actual scope. Book a call to lock this in.
        </p>
        <a href="/contact" data-testid="cost-result-cta" className="mt-8 inline-flex w-full items-center justify-center rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">
          Turn this into a real number
        </a>
      </ToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        setState({ ...state, error: "Please enter a valid email address." });
        return;
      }
      const r = calc();
      try {
        await api.post("/tools/software-cost", {
          project_type: form.project_type,
          roles_count: Number(form.roles) || 0,
          mobile_app: !!form.mobile,
          integrations: Number(form.integrations) || 0,
          timeline: form.timeline,
          cost_band_omr: r.cost_band_omr,
          timeline_range: r.timeline_range,
          email: contact.email,
          name: contact.name,
          company: contact.company,
        });
        setState({ stage: "result", result: r });
      } catch (err) {
        setState({ ...state, error: "Failed to send. Please try again." });
      }
    };
    return (
      <ToolShell eyebrow="Software Cost Estimator" title="See your estimate" step={total} totalSteps={total}>
        <form onSubmit={submit} className="space-y-4" data-testid="cost-gate-form">
          <input placeholder="Your name" data-testid="cost-name" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input required type="email" placeholder="Work email *" data-testid="cost-email" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          <input placeholder="Company" data-testid="cost-company" className="h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          {state.error ? <div className="flex items-center gap-2 text-sm text-wafa-tomato"><AlertCircle className="h-4 w-4" />{state.error}</div> : null}
          <button data-testid="cost-see-result" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep">See my estimate</button>
        </form>
      </ToolShell>
    );
  }

  const canNext = () => {
    if (step === 0) return !!form.project_type;
    if (step === 1) return form.roles !== "" && form.roles !== null;
    if (step === 2) return form.mobile !== null;
    if (step === 3) return form.integrations !== "" && form.integrations !== null;
    if (step === 4) return !!form.timeline;
    return true;
  };

  return (
    <ToolShell eyebrow="Software Cost Estimator" title="A quick indicative estimate." subtitle="Five questions. Real numbers come after a scoping call." step={step + 1} totalSteps={total}>
      {step === 0 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">What kind of project is this?</div>
          <div className="mt-6 space-y-2.5">
            {PROJECT_TYPES.map((p) => (
              <OptionRow key={p.key} value={p.key} current={form.project_type} onChange={(v) => setForm({ ...form, project_type: v })} label={p.label} testId={`cost-project-${p.key}`} />
            ))}
          </div>
        </div>
      )}
      {step === 1 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">How many distinct user roles will use it?</div>
          <div className="mt-6">
            <input type="range" min={1} max={12} step={1} value={form.roles} onChange={(e) => setForm({ ...form, roles: Number(e.target.value) })} data-testid="cost-roles-slider" className="w-full accent-wafa-primary" />
            <div className="mt-2 text-sm text-wafa-ink/70">{form.roles} role{form.roles === 1 ? "" : "s"}</div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">Do you need a mobile app too?</div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <OptionRow value={true} current={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} label="Yes" testId="cost-mobile-yes" />
            <OptionRow value={false} current={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} label="No" testId="cost-mobile-no" />
          </div>
        </div>
      )}
      {step === 3 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">How many third-party integrations?</div>
          <div className="mt-6">
            <input type="range" min={0} max={15} step={1} value={form.integrations} onChange={(e) => setForm({ ...form, integrations: Number(e.target.value) })} data-testid="cost-integrations-slider" className="w-full accent-wafa-primary" />
            <div className="mt-2 text-sm text-wafa-ink/70">{form.integrations} integration{form.integrations === 1 ? "" : "s"}</div>
          </div>
        </div>
      )}
      {step === 4 && (
        <div>
          <div className="font-heading text-xl font-medium text-wafa-deep">What's your timeline?</div>
          <div className="mt-6 space-y-2.5">
            {TIMELINE_OPTS.map((t) => (
              <OptionRow key={t} value={t} current={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} label={t} testId={`cost-timeline-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            ))}
          </div>
        </div>
      )}

      <ToolNav
        onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => { if (step === total - 2) setState({ stage: "gate", error: "" }); else setStep(step + 1); }}
        disableNext={!canNext()}
        nextLabel={step === total - 2 ? "Finish" : "Next"}
      />
    </ToolShell>
  );
}
